/**
 * Copy do modal de bloqueio de cancelamento de expedição — TMS-8134 (ações A4 e A6).
 *
 * Esta é a entrega: o texto que o operador de CD lê às 2h da manhã com um caminhão
 * parado. As 14 guardas do backend passaram a mandar *o objeto que bloqueia*
 * (`isend-backend`, ADR-0005, D-01); aqui esse objeto vira frase.
 *
 * O que a copy corrige, literalmente:
 *
 * - **9 chamados** exibiam `"A carga não pode ter cálculo ou trecho despesa vinculado
 *   para realizar o cancelamento da Expedição."` — idêntica em cargas diferentes, com
 *   botão único "Fechar". Violação sem requisito e sem o objeto. Agora o título nomeia a
 *   ação seguinte e o corpo diz **quantos** trechos e **desde quando**.
 * - **3 chamados** exibiam `"Processo de expedição em andamento, por favor aguarde para
 *   cancelar."` com botão "Close", em inglês. A frase é factualmente falsa: a guarda era
 *   `valor != 3` e disparava igual para Pendente (1), Em andamento (2) e Erro (4). No
 *   SDK-101072 os 10 CT-es estavam em rejeição definitiva da SEFAZ e o motivo aparecia
 *   truncado na coluna como `Rejeição: IE d…`; no SDK-101098 a espera durou 29 horas.
 *   Agora o texto é condicionado ao estado real e **o motivo vai integral**.
 *
 * Dos 23 chamados da TMS-7223, 12 teriam sido resolvidos pelo próprio operador se a tela
 * nomeasse a ação seguinte.
 *
 * Três regras não negociáveis, todas testadas:
 *
 * 1. **Nunca truncar o motivo da SEFAZ/NSTech.** O backend passou a mandá-lo inteiro
 *    exatamente porque a tela o cortava. Cortar aqui reintroduz o defeito.
 * 2. **Nunca escrever "3 horas".** O limite do Vale Pedágio é
 *    `b_config_blogistic.qtde_horas_pedagio`, por cliente, e chega em
 *    `prazo.limite_horas`. Sem esse dado o prazo é **indeterminado** — jamais "ainda dá
 *    tempo".
 * 3. **Ação primária só com `proxima_acao.permitida === true`.** A regra vive em
 *    `normalizarBloqueio`; aqui ela vira a frase que diz *qual* ação falta e por quê.
 *
 * Módulo puro de propósito (sem DOM, sem Vue): ver `jest.config.js`.
 */

import {
  CodigoAcao,
  MotivoIndisponibilidade,
  avaliarAcaoNaSequencia,
  detalhesTecnicos,
  formatarDataHora,
} from "./sequenciaCancelamento";
import { StatusProcessoExpedicaoEnum } from "../../enums/GeneralEnums";

/**
 * As 6 famílias de guarda. Não são um enum do backend: são os 6 textos distintos que a
 * tela precisa ter. `codigo` mapeia para uma delas; o Vale Pedágio se parte em duas
 * porque dentro e fora da janela são situações opostas para o operador.
 */
export const FamiliaBloqueio = Object.freeze({
  DESPESA: "despesa",
  DOCUMENTO_DESPESA: "documento_despesa",
  VALE_PEDAGIO: "vale_pedagio",
  VALE_PEDAGIO_EXPIRADO: "vale_pedagio_expirado",
  NSTECH: "nstech",
  SEM_SAIDA: "sem_saida",
});

/* Códigos publicados por `MotivoBloqueioCancelamentoEnum` (isend-backend) mais os dois
   da recusa local do "Cancelar NSTech" (`bloqueioLocalNstech`). */
const CODIGO = Object.freeze({
  TRECHO_DESPESA: "expedicao_cancelar.trecho_despesa_ativo",
  DESPESA_EM_ANDAMENTO: "expedicao_cancelar.despesa_em_andamento",
  PREFATURA: "expedicao_cancelar.prefatura_despesa",
  PREFATURA_ESTORNO: "expedicao_estornar.prefatura_despesa",
  DOCTRANSP: "expedicao_cancelar.doctransp_auditoria",
  DOCTRANSP_ESTORNO: "expedicao_estornar.doctransp_auditoria",
  VALE_PEDAGIO: "expedicao_cancelar.vale_pedagio_ativo",
  NSTECH_ESTORNO: "expedicao_estornar.nstech_nao_cancelada",
  NSTECH_LOCAL_ANDAMENTO: "expedicao_cancelar_nstech.processo_em_andamento",
  NSTECH_LOCAL_NAO_CONCLUIDA: "expedicao_cancelar_nstech.expedicao_nao_concluida",
  OCORRENCIA: "expedicao_cancelar.ocorrencia_entrega",
  OCORRENCIA_ESTORNO: "expedicao_estornar.ocorrencia_entrega",
  MANIFESTO: "expedicao_cancelar.manifesto_ativo",
  MANIFESTO_ESTORNO: "expedicao_estornar.manifesto_ativo",
  CIOT: "expedicao_cancelar.ciot_ativo",
  CIOT_ESTORNO: "expedicao_estornar.ciot_ativo",
});

const FAMILIA_POR_CODIGO = Object.freeze({
  [CODIGO.TRECHO_DESPESA]: FamiliaBloqueio.DESPESA,
  [CODIGO.PREFATURA]: FamiliaBloqueio.DOCUMENTO_DESPESA,
  [CODIGO.PREFATURA_ESTORNO]: FamiliaBloqueio.DOCUMENTO_DESPESA,
  [CODIGO.DOCTRANSP]: FamiliaBloqueio.DOCUMENTO_DESPESA,
  [CODIGO.DOCTRANSP_ESTORNO]: FamiliaBloqueio.DOCUMENTO_DESPESA,
  [CODIGO.VALE_PEDAGIO]: FamiliaBloqueio.VALE_PEDAGIO,
  [CODIGO.NSTECH_ESTORNO]: FamiliaBloqueio.NSTECH,
  [CODIGO.NSTECH_LOCAL_ANDAMENTO]: FamiliaBloqueio.NSTECH,
  [CODIGO.NSTECH_LOCAL_NAO_CONCLUIDA]: FamiliaBloqueio.NSTECH,
});

/**
 * Rótulo canônico de cada ação, em pt-BR. O backend também manda um `rotulo`, mas a
 * tela é a dona da copy: o CT04 pede literalmente **"Voltar para montagem"** e o
 * catálogo do backend diz "Voltar a carga para a montagem". Código desconhecido
 * mantém o rótulo do backend — é o que permite uma ação nova aparecer sem release
 * do front.
 */
const ROTULO_ACAO = Object.freeze({
  [CodigoAcao.ESTORNAR_CALCULO]: "Estornar cálculo agora",
  [CodigoAcao.CANCELAR_VALE_PEDAGIO]: "Cancelar o Vale Pedágio agora",
  [CodigoAcao.CANCELAR_NSTECH]: "Cancelar a carga na NSTech",
  [CodigoAcao.CANCELAR_EXPEDICAO]: "Cancelar a expedição",
  [CodigoAcao.VOLTAR_PARA_MONTAGEM]: "Voltar para montagem",
  [CodigoAcao.ACOMPANHAR_STATUS]: "Acompanhar status",
  [CodigoAcao.ABRIR_CHAMADO]: "Abrir chamado com estes dados",
  [CodigoAcao.CANCELAR_CIOT]: "Cancelar o CIOT agora",
});

/* Etapas da composição chegam como objetos (`{ordem, endpoint, metodo, condicao}`) —
   contrato C-03. Renderizar isso cru daria "[object Object]" na tela. */
const ETAPA_POR_ENDPOINT = Object.freeze({
  "POST /api/expedicao/cancelar": "Cancelar a expedição",
  "PATCH /api/montagemcarga/cancelar": "Cancelar a montagem e devolver a carga para Pendente",
  "POST /api/expedicao/calculo/estornar": "Estornar o cálculo de despesa",
  "POST /api/expedicao/integracao/manual": "Executar a integração manualmente",
});

const CONDICAO_LEGIVEL = Object.freeze({
  "status_processo_expedicao == 2": "somente se a expedição estiver Em andamento",
});

/* A guarda de estorno manda a etapa como código estável. O operador não lê snake_case. */
const ETAPA_LEGIVEL = Object.freeze({
  cancelamento_carga_nstech: "cancelamento da carga na NSTech",
  carregamento_despesa: "carregamento de despesa",
  emissao_cte: "emissão dos CT-es",
});

const TITULO_PADRAO = "Cancelamento bloqueado";

/* Por que o passo está riscado na lista da sequência. */
const EXPLICACAO_INDISPONIBILIDADE = Object.freeze({
  [MotivoIndisponibilidade.SEM_PERMISSAO]:
    "seu perfil não tem permissão para este passo",
  [MotivoIndisponibilidade.ESTADO_INCOMPATIVEL]:
    "o estado atual da carga não permite este passo",
  [MotivoIndisponibilidade.PASSO_ANTERIOR_PENDENTE]:
    "depende de um passo anterior",
  [MotivoIndisponibilidade.PRAZO_EXPIRADO]:
    "a janela do fornecedor já expirou",
  [MotivoIndisponibilidade.ESTORNO_NAO_RESOLVE]:
    "não resolve sozinho: o Vale Pedágio já passou da janela",
});

const AVISO_CHAMADO =
  "Abra um chamado com os dados abaixo: eles já vão preenchidos e o suporte " +
  "localiza a ocorrência por eles.";

/* ------------------------------------------------------------------------- *
 * Utilitários locais
 * ------------------------------------------------------------------------- */

function texto(valor) {
  return valor == null || valor === "" ? "" : String(valor);
}

function numero(valor) {
  if (valor == null || valor === "") {
    return null;
  }
  const convertido = Number(valor);
  return Number.isFinite(convertido) ? convertido : null;
}

function bruto(bloqueio) {
  const objeto = (bloqueio && bloqueio.objeto) || {};
  return objeto.bruto && typeof objeto.bruto === "object" ? objeto.bruto : {};
}

/** Frase opcional: só entra quando há dado. Nada de "criados em null". */
function trecho(condicao, frase) {
  return condicao ? frase : "";
}

/**
 * Estado real da expedição por trás da recusa. A guarda remota manda em
 * `objeto.status_processo_expedicao`; a recusa local também, e o envelope do chamado
 * repete o valor — os três caminhos convergem aqui.
 *
 * @param {object|null} bloqueio
 * @returns {number|null}
 */
function statusProcesso(bloqueio) {
  const doObjeto = numero(bruto(bloqueio).status_processo_expedicao);
  if (doObjeto != null) {
    return doObjeto;
  }

  const dados =
    (bloqueio && bloqueio.acaoAlternativa && bloqueio.acaoAlternativa.dados) || {};
  const doChamado = numero(dados.status_processo_expedicao);
  if (doChamado != null) {
    return doChamado;
  }

  /* Último recurso: o próprio código da recusa local já carrega o estado. */
  if (bloqueio && bloqueio.codigo === CODIGO.NSTECH_LOCAL_ANDAMENTO) {
    return StatusProcessoExpedicaoEnum.EM_ANDAMENTO;
  }

  return null;
}

/* ------------------------------------------------------------------------- *
 * Rótulos de ação
 * ------------------------------------------------------------------------- */

/**
 * @param {string|null} codigo
 * @param {string|null} rotuloDoBackend
 * @returns {string} nunca vazio — botão sem texto é botão invisível
 */
export function rotuloDaAcao(codigo, rotuloDoBackend) {
  return ROTULO_ACAO[codigo] || texto(rotuloDoBackend) || "Continuar";
}

function descreverEtapa(etapa) {
  if (typeof etapa === "string") {
    return etapa;
  }

  if (!etapa || typeof etapa !== "object") {
    return "";
  }

  const chave = `${texto(etapa.metodo).toUpperCase()} ${texto(etapa.endpoint)}`.trim();
  /* Endpoint sem mapa: a etapa é descartada. Mostrar o caminho cru da API não
     diz nada ao operador. */
  const descricao = ETAPA_POR_ENDPOINT[chave] || "";
  const condicao = CONDICAO_LEGIVEL[texto(etapa.condicao)] || "";

  if (!descricao) {
    return "";
  }

  return condicao ? `${descricao} (${condicao})` : descricao;
}

function normalizarAcaoPrimaria(bloqueio) {
  const acao = bloqueio && bloqueio.acaoPrimaria;
  if (!acao) {
    return null;
  }

  return {
    codigo: acao.codigo,
    rotulo: rotuloDaAcao(acao.codigo, acao.rotulo),
    etapas: (acao.etapas || []).map(descreverEtapa).filter((item) => item !== ""),
  };
}

/**
 * Por que um passo aparece riscado na lista. Vive aqui porque é copy: o modal
 * apenas a renderiza, e o aviso de sequência a reusa.
 *
 * @param {string|null|undefined} motivo valor de `MotivoIndisponibilidade`
 * @returns {string}
 */
export function explicacaoIndisponibilidade(motivo) {
  return EXPLICACAO_INDISPONIBILIDADE[motivo] || "indisponível agora";
}

/**
 * O botão primário cruzado com a sequência exibida — correção do bloqueador da
 * TMS-8133.
 *
 * `proxima_acao` responde "qual ação desbloqueia esta guarda", não "qual ação é
 * segura agora": para `TrechoDespesaAtivo` o backend devolve `estornar_calculo`
 * mesmo quando a NSTech ainda está pendente, e estornar naquele momento leva
 * `status_processo_expedicao` a 2, que é justamente o que inviabiliza o passo 1.
 * Sem este cruzamento, a mesma tela exibia "passo 2 depende de um passo anterior"
 * e oferecia o botão que executa o passo 2.
 *
 * As duas condições são **cumulativas**: `proxima_acao.permitida === true` (resolvida
 * em `normalizarBloqueio`) e a ação ser o próximo passo disponível da sequência.
 * Quando só a segunda falha, a tela degrada para o passo que precisa vir antes —
 * e, se nem ele for executável, para a ação alternativa.
 *
 * @param {object|null} bloqueio view-model de `normalizarBloqueio`
 * @param {Array|null|undefined} passos saída de `passosPendentes`
 * @returns {{acaoPrimaria: object|null, avisoSequencia: string}}
 */
function acaoPrimariaNaSequencia(bloqueio, passos) {
  const acao = normalizarAcaoPrimaria(bloqueio);

  if (!acao) {
    return { acaoPrimaria: null, avisoSequencia: "" };
  }

  const avaliacao = avaliarAcaoNaSequencia(acao.codigo, passos);

  if (avaliacao.emSequencia) {
    return { acaoPrimaria: acao, avisoSequencia: "" };
  }

  const bloqueador = avaliacao.passoBloqueador;
  const substituto = avaliacao.substituto;
  const mesmoPasso = Boolean(bloqueador && bloqueador.codigo === acao.codigo);

  const abertura = mesmoPasso
    ? `"${acao.rotulo}" está indisponível nesta carga agora — ` +
      `${explicacaoIndisponibilidade(bloqueador.motivoIndisponivel)}.`
    : `"${acao.rotulo}" não é o próximo passo desta carga: ` +
      `"${rotuloDaAcao(
        bloqueador ? bloqueador.codigo : null,
        bloqueador ? bloqueador.rotulo : null
      )}" precisa vir antes. Fazer isso agora impediria esse passo: ao estornar ` +
      'o cálculo a expedição volta para "Em andamento", e a NSTech só aceita ' +
      "cancelar uma expedição concluída.";

  if (!substituto) {
    return {
      acaoPrimaria: null,
      avisoSequencia: `${abertura} Nenhum passo desta sequência está executável por aqui agora. ${AVISO_CHAMADO}`,
    };
  }

  return {
    acaoPrimaria: {
      codigo: substituto.codigo,
      rotulo: rotuloDaAcao(substituto.codigo, substituto.rotulo),
      etapas: [],
    },
    avisoSequencia: `${abertura} O botão abaixo executa esse passo anterior. Depois dele, clique em Cancelar de novo.`,
  };
}

/* ------------------------------------------------------------------------- *
 * Famílias
 * ------------------------------------------------------------------------- */

/**
 * @param {object|null} bloqueio view-model de `normalizarBloqueio`
 * @returns {string} um valor de `FamiliaBloqueio`
 */
export function familiaDoBloqueio(bloqueio) {
  if (!bloqueio || typeof bloqueio !== "object") {
    return FamiliaBloqueio.SEM_SAIDA;
  }

  const familia = FAMILIA_POR_CODIGO[bloqueio.codigo];

  if (familia === FamiliaBloqueio.VALE_PEDAGIO) {
    /* Prazo ausente NÃO é "dentro da janela": é limite desconhecido. O backend omite
       a chave de propósito para a tela não dizer que ainda dá tempo. */
    return bloqueio.prazo && bloqueio.prazo.expirado
      ? FamiliaBloqueio.VALE_PEDAGIO_EXPIRADO
      : FamiliaBloqueio.VALE_PEDAGIO;
  }

  return familia || FamiliaBloqueio.SEM_SAIDA;
}

/* --- família A — trecho de despesa ativo (9 dos 23 chamados) --------------- */

function copyDespesa(bloqueio) {
  const objeto = bloqueio.objeto || {};
  const quantidade = objeto.quantidade;
  const desde = formatarDataHora(objeto.desde);
  const singular = quantidade === 1;

  const contagem = singular
    ? "1 trecho de despesa ativo"
    : `${quantidade == null ? "" : `${quantidade} `}trechos de despesa ativos`;

  const criacao = trecho(
    desde !== "",
    `, ${singular ? "criado" : "criados"} em ${desde}`
  );

  /* TMS-7223: com o Vale Pedágio fora da janela, estornar o cálculo não libera o
     cancelamento (o backend manda `proxima_acao = null`). Mandar estornar seria
     mandar o operador para um beco sem botão. O limite vem só de `prazo.limiteHoras`;
     sem ele o texto fica neutro, sem número. */
  const prazo = bloqueio.prazo;
  if (prazo && prazo.expirado) {
    const janela =
      prazo.limiteHoras == null
        ? "janela de cancelamento"
        : `janela de ${prazo.limiteHoras} horas para cancelamento`;

    return {
      titulo: "Esta carga precisa de apoio do suporte para cancelar",
      corpo:
        `Esta carga tem ${contagem}${criacao}, e o Vale Pedágio dela já passou da ` +
        `${janela}. ` +
        "Estornar o cálculo agora não libera o cancelamento. " +
        "Abra o chamado com estes dados.",
    };
  }

  return {
    titulo: "Antes de cancelar, é preciso estornar o cálculo de despesa",
    corpo:
      `Esta carga tem ${contagem}${criacao}. ` +
      "O cancelamento da expedição só é liberado depois que eles forem estornados.",
  };
}

/* --- família B — pré-fatura e auditoria de frete --------------------------- */

function copyDocumentoDespesa(bloqueio) {
  const objeto = bloqueio.objeto || {};
  const dados = bruto(bloqueio);
  const desde = formatarDataHora(objeto.desde);

  if (
    bloqueio.codigo === CODIGO.DOCTRANSP ||
    bloqueio.codigo === CODIGO.DOCTRANSP_ESTORNO
  ) {
    const documento = texto(dados.num_doc) || texto(dados.doctransp_id);

    return {
      titulo: "O cancelamento depende de um documento na auditoria de frete",
      corpo:
        `Esta carga tem o documento de transporte ${documento || "de despesa"} na ` +
        `auditoria de frete${trecho(desde !== "", ` desde ${desde}`)}. ` +
        "Enquanto ele estiver vinculado, a expedição não pode ser cancelada — " +
        "quem desvincula o documento é a auditoria de frete.",
    };
  }

  /* SDK-103674 foi a única mensagem que nomeava o objeto, e nomeava concatenando o
     número de controle no texto. Agora ele vem estruturado. */
  const numControle = texto(objeto.numControle) || texto(dados.num_controle);

  return {
    titulo: "O cancelamento depende de uma pré-fatura de despesa",
    corpo:
      "Esta carga tem documento de despesa vinculado à pré-fatura " +
      `${numControle || "de despesa"}${trecho(desde !== "", `, criada em ${desde}`)}. ` +
      "Enquanto a pré-fatura existir, a expedição não pode ser cancelada — " +
      "quem desvincula o documento é a auditoria de frete.",
  };
}

/* --- famílias C e D — Vale Pedágio (A6) ----------------------------------- */

function copyValePedagio(bloqueio) {
  const prazo = bloqueio.prazo;
  const compradoEm = formatarDataHora(
    (prazo && prazo.compradoEm) || (bloqueio.objeto || {}).desde
  );

  const compra = trecho(compradoEm !== "", `, comprado em ${compradoEm}`);

  const indeterminado = !prazo || prazo.limiteHoras == null;

  const alerta = indeterminado
    ? " O prazo desta janela não está configurado para este cliente, então o iSend " +
      "não consegue informar quanto tempo resta — trate como indeterminado e cancele " +
      "o Vale Pedágio o quanto antes."
    : "";

  return {
    titulo: "Cancele o Vale Pedágio antes de cancelar a expedição",
    corpo:
      `Esta carga tem um Vale Pedágio ativo${compra}. ` +
      "A janela de cancelamento é regra do fornecedor Target: passado o limite, o " +
      "cancelamento deixa de ser aceito e a carga fica sem saída pela tela." +
      alerta,
  };
}

function copyValePedagioExpirado(bloqueio) {
  const prazo = bloqueio.prazo || {};
  const compradoEm = formatarDataHora(
    prazo.compradoEm || (bloqueio.objeto || {}).desde
  );
  const limite = prazo.limiteHoras;

  return {
    titulo: "A janela de cancelamento do Vale Pedágio já fechou",
    corpo:
      `O Vale Pedágio desta carga foi comprado${trecho(
        compradoEm !== "",
        ` em ${compradoEm}`
      )} e a Target aceita o cancelamento apenas dentro de ` +
      `${limite == null ? "uma janela definida pelo fornecedor" : `${limite}h`}` +
      ". Essa é uma regra do fornecedor, e não uma falha do iSend: o cancelamento " +
      "precisa ser feito no portal da Target e o cancelamento da expedição retomado " +
      "aqui depois.",
  };
}

/* --- família E — NSTech / SEFAZ (CT03 e CT04) ----------------------------- */

function copyNstech(bloqueio) {
  const objeto = bloqueio.objeto || {};
  const status = statusProcesso(bloqueio);
  const etapa = ETAPA_LEGIVEL[texto(objeto.etapa)] || "";
  /* Etapa sem mapa fica de fora: o código cru (snake_case) não diz nada ao operador.
     Ele continua em `detalhes` ("Etapa"). */
  const motivo = texto(objeto.motivo);
  const desde = formatarDataHora(objeto.desde);

  if (status === StatusProcessoExpedicaoEnum.EM_ANDAMENTO) {
    return {
      titulo: "A expedição ainda está processando",
      corpo:
        "A expedição desta carga ainda está em processamento" +
        `${trecho(desde !== "", `, iniciada em ${desde}`)}. ` +
        "Enquanto ela não concluir, o iSend não consegue cancelar a carga na NSTech. " +
        'Se o processamento não terminar, a saída é "Voltar para montagem".',
    };
  }

  if (status === StatusProcessoExpedicaoEnum.ERRO) {
    return {
      titulo: "Não é possível cancelar na NSTech agora",
      corpo: motivo
        ? /* Integral. Truncar aqui é o defeito do SDK-101072 (`Rejeição: IE d…`). */
          `A expedição parou com erro${trecho(etapa !== "", ` em ${etapa}`)}: ${motivo}`
        : `A expedição parou com erro${trecho(etapa !== "", ` em ${etapa}`)}. ` +
          "A NSTech não registrou o motivo desta carga. " +
          "Abra um chamado com os dados abaixo para o suporte localizar a ocorrência.",
    };
  }

  if (status === StatusProcessoExpedicaoEnum.PENDENTE) {
    return {
      titulo: "Não é possível cancelar na NSTech agora",
      corpo:
        "A expedição desta carga está Pendente: não existe expedição concluída para " +
        "cancelar na NSTech. Para liberar a carga, devolva-a para a montagem.",
    };
  }

  return {
    titulo: "Não é possível cancelar na NSTech agora",
    corpo:
      motivo ||
      texto(bloqueio.mensagem) ||
      "A carga precisa estar cancelada na NSTech para seguir com esta ação.",
  };
}

/* --- família F — sem saída automatizada ----------------------------------- */

function copySemSaida(bloqueio) {
  const objeto = bloqueio.objeto || {};
  const dados = bruto(bloqueio);
  const desde = formatarDataHora(objeto.desde);

  switch (bloqueio.codigo) {
    case CODIGO.DESPESA_EM_ANDAMENTO:
      return {
        titulo: "O cálculo de despesa desta carga ainda está processando",
        corpo:
          "O iSend está calculando a despesa desta carga neste momento. Aguarde a " +
          "conclusão do cálculo e tente cancelar de novo — os trechos de " +
          "despesa são gerados pelo próprio sistema, não pelo operador.",
      };

    case CODIGO.OCORRENCIA:
    case CODIGO.OCORRENCIA_ESTORNO:
      return {
        titulo: "Esta carga já tem ocorrência de entrega registrada",
        corpo:
          `A nota ${texto(dados.docfis_id) || "desta carga"} registrou a ocorrência ` +
          `"${texto(dados.ocorrencia) || "de entrega"}"` +
          `${trecho(desde !== "", ` em ${desde}`)}. ` +
          "Carga com entrega registrada não pode ter a expedição cancelada pela tela.",
      };

    case CODIGO.MANIFESTO:
    case CODIGO.MANIFESTO_ESTORNO:
      return {
        titulo: "O manifesto desta carga ainda está ativo",
        corpo:
          `O manifesto ${texto(dados.num_manifesto) || "desta carga"} continua ativo` +
          `${trecho(desde !== "", ` desde ${desde}`)}. ` +
          "Cancele o manifesto antes de cancelar a expedição.",
      };

    case CODIGO.CIOT:
    case CODIGO.CIOT_ESTORNO:
      return {
        titulo: "O CIOT desta carga ainda está ativo",
        corpo:
          `O CIOT ${texto(dados.num_ciot) || "desta carga"} continua ativo. ` +
          "Cancele o CIOT antes de cancelar a expedição.",
      };

    default:
      /* Código que a tela ainda não conhece. O `message` do backend é auto-suficiente
         por contrato (ADR-0005, D-01, restrição 5) — exibi-lo é melhor do que
         inventar um texto genérico. */
      return {
        titulo: texto(bloqueio.titulo) || "Não foi possível concluir a ação",
        corpo: texto(bloqueio.mensagem),
      };
  }
}

const COPY_POR_FAMILIA = Object.freeze({
  [FamiliaBloqueio.DESPESA]: copyDespesa,
  [FamiliaBloqueio.DOCUMENTO_DESPESA]: copyDocumentoDespesa,
  [FamiliaBloqueio.VALE_PEDAGIO]: copyValePedagio,
  [FamiliaBloqueio.VALE_PEDAGIO_EXPIRADO]: copyValePedagioExpirado,
  [FamiliaBloqueio.NSTECH]: copyNstech,
  [FamiliaBloqueio.SEM_SAIDA]: copySemSaida,
});

/* ------------------------------------------------------------------------- *
 * Ações secundárias, avisos e detalhes
 * ------------------------------------------------------------------------- */

function acaoSecundariaDe(bloqueio) {
  const objeto = bloqueio.objeto || {};

  if (!objeto.ids || objeto.ids.length === 0) {
    return null;
  }

  return {
    codigo: CodigoAcao.VER_OBJETO,
    rotulo:
      objeto.tipo === "pedido_unidade_carga_trecho"
        ? "Ver os trechos"
        : "Ver os detalhes da carga",
  };
}

/**
 * A frase que substitui o botão quando a ação existe e o usuário não pode executá-la.
 * Nomear a ação é o ponto: dos 23 chamados nenhum veio de falta de permissão, mas os
 * 20 usuários presos no passo 2 (e os 66 que perderam "Cancelar carga na NSTech" com o
 * gate corrigido na TMS-8135) precisam saber o que pedir.
 */
function avisoSemAcaoPrimariaDe(bloqueio, familia) {
  if (bloqueio && bloqueio.acaoPrimaria) {
    return "";
  }

  const indisponivel = (bloqueio && bloqueio.acaoIndisponivel) || null;
  const nome = indisponivel
    ? `"${rotuloDaAcao(indisponivel.codigo, indisponivel.rotulo)}"`
    : "";

  const motivo = bloqueio && bloqueio.motivoSemAcaoPrimaria;

  if (motivo === MotivoIndisponibilidade.SEM_PERMISSAO) {
    return (
      /* O código da permissão não vai para o texto da tela: vai em `detalhes`
         ("Permissão necessária"), que é o que o suporte precisa para liberar. */
      `Seu perfil não tem permissão para ${nome || "a próxima ação desta sequência"}` +
      `. ${AVISO_CHAMADO}`
    );
  }

  if (motivo === MotivoIndisponibilidade.ESTADO_INCOMPATIVEL) {
    return (
      `O estado atual da carga não permite ${nome || "nenhuma ação automática por aqui"}` +
      `. ${AVISO_CHAMADO}`
    );
  }

  if (familia === FamiliaBloqueio.VALE_PEDAGIO_EXPIRADO) {
    return (
      "Não há cancelamento automático depois que a janela do fornecedor fecha — " +
      "alterar a carga só no iSend deixaria o iSend e a Target com informações diferentes. " +
      AVISO_CHAMADO
    );
  }

  return `Não há ação automática para este bloqueio na tela. ${AVISO_CHAMADO}`;
}

/**
 * Motivo por documento fiscal (CT04). Cada CT-e rejeitado tem o seu, e no SDK-101072
 * eram 10. Vai nos detalhes técnicos, integral, um por linha.
 */
function detalhesDosMotivos(bloqueio) {
  const motivos = bruto(bloqueio).motivos;

  if (!Array.isArray(motivos)) {
    return [];
  }

  return motivos
    .filter((item) => item && texto(item.mensagem_retorno) !== "")
    .map((item) => ({
      rotulo: `Retorno do documento ${texto(item.docfis_id) || "sem número"}`,
      valor: texto(item.mensagem_retorno),
    }));
}

/* ------------------------------------------------------------------------- *
 * Entrada pública
 * ------------------------------------------------------------------------- */

/**
 * Texto completo do modal para um bloqueio já normalizado.
 *
 * `passos` é a sequência que a própria tela está exibindo (saída de
 * `passosPendentes`). Ela entra aqui porque o botão primário precisa ser coerente
 * com essa lista: oferecer a ação de um passo que a lista declara indisponível foi
 * o bloqueador reprovado pelo QA na TMS-8133. Sem `passos` a copy se comporta como
 * antes — nenhum juízo de ordem é possível sem a sequência.
 *
 * @param {object|null} bloqueio saída de `normalizarBloqueio`
 * @param {Array} [passos] saída de `passosPendentes`
 * @returns {{familia: string, titulo: string, corpo: string, prazo: string,
 *            prazoExpirado: boolean, prazoIndeterminado: boolean,
 *            acaoPrimaria: {codigo: string, rotulo: string, etapas: string[]}|null,
 *            acaoSecundaria: {codigo: string, rotulo: string}|null,
 *            acaoAlternativa: {codigo: string, rotulo: string}|null,
 *            avisoSequencia: string, avisoSemAcaoPrimaria: string,
 *            detalhes: Array<{rotulo: string, valor: string}>}}
 */
export function copyDoBloqueio(bloqueio, passos) {
  if (!bloqueio || typeof bloqueio !== "object") {
    return {
      familia: FamiliaBloqueio.SEM_SAIDA,
      titulo: TITULO_PADRAO,
      corpo: "",
      prazo: "",
      prazoExpirado: false,
      prazoIndeterminado: false,
      acaoPrimaria: null,
      acaoSecundaria: null,
      acaoAlternativa: null,
      avisoSequencia: "",
      avisoSemAcaoPrimaria: `Não há ação automática para este bloqueio na tela. ${AVISO_CHAMADO}`,
      detalhes: [],
    };
  }

  const familia = familiaDoBloqueio(bloqueio);
  const { titulo, corpo } = COPY_POR_FAMILIA[familia](bloqueio);
  const prazo = bloqueio.prazo || null;
  const { acaoPrimaria, avisoSequencia } = acaoPrimariaNaSequencia(
    bloqueio,
    passos
  );

  const detalhes = detalhesTecnicos(bloqueio).concat(detalhesDosMotivos(bloqueio));

  /* A `message` do backend é o fallback de toda tela que não lê `bloqueio`. Quando a
     copy a substitui, ela não some: fica nos detalhes, para o chamado citar a frase
     que o operador encontraria na aba Erros. */
  const mensagem = texto(bloqueio.mensagem);
  if (mensagem !== "" && mensagem !== corpo) {
    detalhes.push({ rotulo: "Mensagem do sistema", valor: mensagem });
  }

  return {
    familia,
    titulo: titulo || texto(bloqueio.titulo) || TITULO_PADRAO,
    corpo,
    prazo: (prazo && prazo.rotulo) || "",
    prazoExpirado: Boolean(prazo && prazo.expirado),
    prazoIndeterminado:
      familia === FamiliaBloqueio.VALE_PEDAGIO && (!prazo || prazo.limiteHoras == null),
    acaoPrimaria,
    avisoSequencia,
    acaoSecundaria: acaoSecundariaDe(bloqueio),
    acaoAlternativa: bloqueio.acaoAlternativa
      ? {
          codigo: bloqueio.acaoAlternativa.codigo,
          rotulo: rotuloDaAcao(
            bloqueio.acaoAlternativa.codigo,
            bloqueio.acaoAlternativa.rotulo
          ),
        }
      : null,
    avisoSemAcaoPrimaria: avisoSemAcaoPrimariaDe(bloqueio, familia),
    detalhes,
  };
}
